<template>
  <div
    class="min-h-screen bg-slate-50/60 pb-28 text-slate-800 font-sans antialiased"
  >
    <main
      class="mx-auto max-w-7xl px-3.5 sm:px-6 lg:px-8 py-5 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- LOADING SKELETON -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div
          class="hidden md:block h-64 w-full animate-pulse rounded-3xl bg-slate-200/70"
        ></div>
        <div
          class="h-96 w-full animate-pulse rounded-3xl bg-slate-200/70"
        ></div>
      </div>

      <div
        v-else
        class="space-y-6 sm:space-y-8"
      >
        <!-- =====================================================
             SECTION 1: LIVE EDITORIAL PREVIEWS (DESKTOP & TABLET ONLY)
        ===================================================== -->
        <section class="hidden md:block space-y-4">
          <div class="flex items-center justify-between px-1">
            <div class="flex items-center gap-2.5">
              <span class="relative flex h-2.5 w-2.5">
                <span
                  class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"
                ></span>
              </span>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5"
              >
                <EyeIcon class="h-4 w-4 text-indigo-600" />
                Live Front-End Simulation
              </h3>
            </div>

            <div class="flex items-center gap-2">
              <span
                class="rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700"
              >
                Realtime Mirror
              </span>
            </div>
          </div>

          <!-- 1. Shaping Bright Futures (Pillars) Live Preview -->
          <div
            class="rounded-3xl border border-[#E1D8C6] bg-[#FAF7F1] p-6 sm:p-8 space-y-6 shadow-xs"
          >
            <div class="max-w-xl">
              <span class="eyebrow">Our commitment</span>
              <h2
                class="font-display font-semibold text-2xl sm:text-3xl text-[#11151F] mt-2 tracking-tight"
              >
                Shaping Bright Futures
              </h2>
              <p class="text-sm text-[#4B5262] mt-1.5">
                Commitments that guide every decision made on this campus.
              </p>
            </div>

            <div
              class="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-stretch"
            >
              <div
                v-for="(pillar, pIdx) in formData.pillars"
                :key="pillar._id || pIdx"
                class="rounded-2xl border border-[#E1D8C6] bg-white p-6 relative flex flex-col justify-between shadow-xs transition hover:border-[#B8863B]"
              >
                <span
                  class="font-serif italic text-xl text-[#B8863B] font-normal absolute top-5 right-6"
                >
                  {{ pillar.num || String.fromCharCode(65 + pIdx) + "." }}
                </span>

                <div class="space-y-4">
                  <div
                    class="w-11 h-11 rounded-full bg-[#11151F] text-[#B8863B] flex items-center justify-center text-base shadow-sm"
                  >
                    <i :class="['fas', pillar.icon || 'fa-graduation-cap']"></i>
                  </div>

                  <h4
                    class="font-bold text-base sm:text-lg text-[#11151F] tracking-tight pr-6"
                  >
                    {{ pillar.title || "Pillar Title" }}
                  </h4>

                  <p
                    class="text-xs sm:text-[13px] text-[#4B5262] leading-relaxed"
                  >
                    {{ pillar.description || "Pillar description details..." }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- 2. What Parents Say (Testimonials) Live Preview -->
          <div
            class="rounded-3xl border border-slate-200/90 bg-white p-6 sm:p-10 space-y-6 shadow-md relative overflow-hidden"
          >
            <div class="max-w-xl">
              <span class="eyebrow">Parent voices</span>
              <h2
                class="font-display font-semibold text-2xl sm:text-3xl text-slate-900 mt-2 tracking-tight"
              >
                What Parents Say
              </h2>
            </div>

            <div
              class="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-stretch"
            >
              <div
                v-for="(testi, tIdx) in formData.testimonials"
                :key="testi._id || tIdx"
                class="rounded-2xl border border-slate-200/90 bg-slate-50/50 p-6 relative flex flex-col justify-between shadow-xs"
              >
                <span
                  class="font-serif italic text-5xl text-slate-200 leading-none absolute top-4 right-6 select-none"
                  >&rdquo;</span
                >

                <p
                  class="font-serif italic text-slate-800 text-sm sm:text-[15px] leading-relaxed relative z-10 mb-6"
                >
                  "{{
                    testi.quote ||
                    testi.review ||
                    "Parent testimonial quote goes here..."
                  }}"
                </p>

                <div
                  class="flex items-center gap-3 pt-4 border-t border-slate-200/60"
                >
                  <div
                    class="w-10 h-10 rounded-full border border-[#B8863B] overflow-hidden bg-slate-200 shrink-0 flex items-center justify-center"
                  >
                    <img
                      v-if="testi.previewUrl || testi.image"
                      :src="testi.previewUrl || testi.image"
                      :alt="testi.name"
                      class="w-full h-full object-cover rounded-full"
                    />
                    <UserIcon
                      v-else
                      class="h-5 w-5 text-slate-400"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <h5
                      class="font-bold text-xs sm:text-sm text-slate-900 truncate"
                    >
                      {{ testi.name || "Parent Name" }}
                    </h5>
                    <p class="text-[11px] text-slate-500 font-medium truncate">
                      {{ testi.role || "Parent" }}
                    </p>
                    <div
                      class="flex items-center gap-0.5 text-[#B8863B] text-[10px] mt-0.5"
                    >
                      <i
                        v-for="s in Number(testi.rating || 5)"
                        :key="s"
                        class="fas fa-star"
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- =====================================================
             SECTION 2: EDITORIAL FORMS (RESPONSIVE FOR ALL SCREENS)
        ===================================================== -->
        <div
          class="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start"
        >
          <!-- LEFT COLUMN: SHAPING BRIGHT FUTURES (PILLARS) BUILDER -->
          <section
            class="lg:col-span-6 rounded-3xl border border-slate-200/90 bg-white p-4 sm:p-7 shadow-xs space-y-4 sm:space-y-5"
          >
            <div
              class="flex items-center justify-between border-b border-slate-100 pb-3.5 sm:pb-4"
            >
              <div class="flex items-center space-x-2.5 min-w-0">
                <div
                  class="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"
                >
                  <GraduationCapIcon class="h-4 w-4" />
                </div>
                <div class="min-w-0">
                  <h2
                    class="text-sm sm:text-base font-bold text-slate-900 leading-tight truncate"
                  >
                    Shaping Bright Futures (Pillars)
                  </h2>
                  <p class="text-[11px] text-slate-500 truncate">
                    Core institutional commitments &amp; pillars
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-xl bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100 active:scale-95 transition cursor-pointer shrink-0"
                @click="addPillar"
              >
                <PlusIcon class="h-3.5 w-3.5 text-amber-600" />
                <span>Add Pillar</span>
              </button>
            </div>

            <!-- Pillars List -->
            <div class="space-y-3.5 sm:space-y-4">
              <div
                v-for="(pillar, index) in formData.pillars"
                :key="pillar._id || index"
                class="rounded-2xl border border-slate-200/90 bg-slate-50/60 p-3.5 sm:p-5 space-y-3 relative group"
              >
                <div
                  class="flex items-center justify-between border-b border-slate-200/70 pb-2.5"
                >
                  <span
                    class="font-mono text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 truncate"
                  >
                    <span
                      class="w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center text-[9.5px] shrink-0"
                    >
                      {{ index + 1 }}
                    </span>
                    Pillar Card #{{ index + 1 }}
                  </span>

                  <button
                    type="button"
                    class="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                    title="Delete Pillar"
                    @click="removePillar(index)"
                  >
                    <Trash2Icon class="h-4 w-4" />
                  </button>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div>
                    <label
                      class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                      >Index Label (num)</label
                    >
                    <input
                      v-model="pillar.num"
                      type="text"
                      placeholder="A."
                      class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                      >Icon Class</label
                    >
                    <input
                      v-model="pillar.icon"
                      type="text"
                      placeholder="fa-graduation-cap"
                      class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-mono text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                      >Title *</label
                    >
                    <input
                      v-model="pillar.title"
                      type="text"
                      placeholder="Comprehensive Curriculum"
                      class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label
                    class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                    >Description</label
                  >
                  <textarea
                    v-model="pillar.description"
                    rows="2"
                    placeholder="Describe this pillar commitment..."
                    class="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 leading-relaxed focus:border-indigo-500 focus:outline-none"
                  ></textarea>
                </div>
              </div>

              <div
                v-if="formData.pillars.length === 0"
                class="p-6 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50 space-y-2"
              >
                <GraduationCapIcon class="mx-auto h-7 w-7 text-slate-300" />
                <p class="text-xs text-slate-500 font-medium">
                  No commitment pillars configured.
                </p>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-100 text-xs font-bold text-amber-800 hover:bg-amber-100 cursor-pointer"
                  @click="addPillar"
                >
                  <PlusIcon class="h-3.5 w-3.5" />
                  <span>Add First Pillar</span>
                </button>
              </div>
            </div>
          </section>

          <!-- RIGHT COLUMN: WHAT PARENTS SAY (TESTIMONIALS) BUILDER -->
          <section
            class="lg:col-span-6 rounded-3xl border border-slate-200/90 bg-white p-4 sm:p-7 shadow-xs space-y-4 sm:space-y-5"
          >
            <div
              class="flex items-center justify-between border-b border-slate-100 pb-3.5 sm:pb-4"
            >
              <div class="flex items-center space-x-2.5 min-w-0">
                <div
                  class="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"
                >
                  <AwardIcon class="h-4 w-4" />
                </div>
                <div class="min-w-0">
                  <h2
                    class="text-sm sm:text-base font-bold text-slate-900 leading-tight truncate"
                  >
                    What Parents Say (Testimonials)
                  </h2>
                  <p class="text-[11px] text-slate-500 truncate">
                    Parent reviews, ratings &amp; author avatars
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100 active:scale-95 transition cursor-pointer shrink-0"
                @click="addTestimonial"
              >
                <PlusIcon class="h-3.5 w-3.5 text-indigo-600" />
                <span>Add Review</span>
              </button>
            </div>

            <!-- Testimonials List -->
            <div class="space-y-3.5 sm:space-y-4">
              <div
                v-for="(testi, index) in formData.testimonials"
                :key="testi._id || index"
                class="rounded-2xl border border-slate-200/90 bg-slate-50/60 p-3.5 sm:p-5 space-y-3 relative group"
              >
                <div
                  class="flex items-center justify-between border-b border-slate-200/70 pb-2.5"
                >
                  <span
                    class="font-mono text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 truncate"
                  >
                    <span
                      class="w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center text-[9.5px] shrink-0"
                    >
                      {{ index + 1 }}
                    </span>
                    Testimonial #{{ index + 1 }}
                  </span>

                  <button
                    type="button"
                    class="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                    title="Delete Testimonial"
                    @click="removeTestimonial(index)"
                  >
                    <Trash2Icon class="h-4 w-4" />
                  </button>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div>
                    <label
                      class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                      >Parent Name *</label
                    >
                    <input
                      v-model="testi.name"
                      type="text"
                      placeholder="Dr. Mayuri & Neelav"
                      class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                      >Role / Subtitle</label
                    >
                    <input
                      v-model="testi.role"
                      type="text"
                      placeholder="Parent"
                      class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                      >Rating (1-5)</label
                    >
                    <input
                      v-model.number="testi.rating"
                      type="number"
                      min="1"
                      max="5"
                      placeholder="5"
                      class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label
                    class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                    >Review Quote *</label
                  >
                  <textarea
                    v-model="testi.quote"
                    rows="2"
                    placeholder="Enter parent review statement..."
                    class="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 leading-relaxed focus:border-indigo-500 focus:outline-none"
                  ></textarea>
                </div>

                <!-- Testimonial Author Photo Upload -->
                <div
                  class="pt-2 border-t border-slate-200/70 flex items-center gap-3.5 sm:gap-4"
                >
                  <div
                    class="relative group/pic rounded-xl border-2 border-dashed border-slate-300 overflow-hidden bg-white h-14 w-14 sm:h-16 sm:w-16 flex flex-col items-center justify-center text-center shrink-0 hover:border-indigo-400 transition"
                  >
                    <img
                      v-if="testi.previewUrl || testi.image"
                      :src="testi.previewUrl || testi.image"
                      class="absolute inset-0 w-full h-full object-cover rounded-xl"
                    />
                    <div
                      v-else
                      class="text-slate-400"
                    >
                      <ImageIcon class="mx-auto h-3.5 w-3.5" />
                      <span class="block text-[8px] font-bold uppercase mt-0.5"
                        >Photo</span
                      >
                    </div>

                    <input
                      type="file"
                      accept="image/*"
                      class="absolute inset-0 opacity-0 cursor-pointer z-10"
                      @change="(e) => handleTestimonialPhotoSelect(e, index)"
                    />

                    <button
                      v-if="testi.previewUrl || testi.image"
                      type="button"
                      class="absolute top-1 right-1 z-20 p-0.5 rounded-full bg-slate-900/80 text-white hover:bg-rose-600 transition cursor-pointer"
                      @click.stop="removeTestimonialPhoto(index)"
                    >
                      <XIcon class="h-2.5 w-2.5" />
                    </button>
                  </div>

                  <div class="text-[11px] text-slate-500">
                    <span class="font-bold text-slate-700 block"
                      >Author Avatar</span
                    >
                    <span>Square headshot of parent or family</span>
                  </div>
                </div>
              </div>

              <div
                v-if="formData.testimonials.length === 0"
                class="p-6 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50 space-y-2"
              >
                <AwardIcon class="mx-auto h-7 w-7 text-slate-300" />
                <p class="text-xs text-slate-500 font-medium">
                  No parent testimonials configured.
                </p>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 border border-indigo-100 text-xs font-bold text-indigo-700 hover:bg-indigo-100 cursor-pointer"
                  @click="addTestimonial"
                >
                  <PlusIcon class="h-3.5 w-3.5" />
                  <span>Add First Review</span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- =====================================================
           COMPACT STICKY BOTTOM ACTION BAR
      ================================================     -->
      <div
        class="fixed bottom-3 sm:bottom-4 right-3 sm:right-6 lg:right-8 z-40 max-w-[calc(100vw-1.5rem)] sm:max-w-md"
      >
        <div
          class="rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200/90 p-2.5 sm:p-3 shadow-xl shadow-slate-900/10 flex items-center justify-between gap-3 ring-1 ring-slate-950/5"
        >
          <!-- Status Indicator -->
          <div class="flex items-center gap-2 text-xs font-medium min-w-0">
            <span
              v-if="isSaving"
              class="inline-flex items-center gap-1.5 text-indigo-600 font-semibold truncate"
            >
              <Loader2Icon
                class="h-4 w-4 animate-spin shrink-0 text-indigo-600"
              />
              <span class="truncate">Saving...</span>
            </span>

            <span
              v-else-if="hasUnsavedChanges"
              class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-0.5 sm:px-2.5 sm:py-1 border border-amber-200/80 text-amber-700 font-bold shrink-0 text-[11px] sm:text-xs"
            >
              <span
                class="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse"
              ></span>
              <span>Unsaved</span>
            </span>

            <span
              v-else
              class="inline-flex items-center gap-1.5 text-slate-500 truncate text-[11px] sm:text-xs"
            >
              <CheckCircleIcon class="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              <span class="truncate">Saved</span>
            </span>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center gap-2 shrink-0">
            <button
              type="button"
              class="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition active:scale-95 cursor-pointer"
              :disabled="isSaving"
              @click="confirmReset"
            >
              Discard
            </button>

            <button
              type="button"
              class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-3.5 sm:px-4 py-1.5 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none"
              :disabled="isSaving"
              @click="handleSave"
            >
              <Loader2Icon
                v-if="isSaving"
                class="h-3.5 w-3.5 animate-spin"
              />
              <SaveIcon
                v-else
                class="h-3.5 w-3.5"
              />
              <span class="whitespace-nowrap">{{
                isSaving ? "Saving" : "Save Changes"
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all pillars and parent testimonials
          back to their saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    Image as ImageIcon,
    X as XIcon,
    Eye as EyeIcon,
    User as UserIcon,
    Award as AwardIcon,
    GraduationCap as GraduationCapIcon,
  } from "lucide-vue-next";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);
  const initialStateJson = ref("");

  const formData = reactive({
    // Shaping Bright Futures Pillars
    pillars: [],

    // What Parents Say Testimonials
    testimonials: [],
  });

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  function getCleanState() {
    return {
      pillars: formData.pillars.map((p) => ({
        _id: p._id,
        num: p.num || "",
        icon: p.icon || "",
        title: p.title || "",
        description: p.description || "",
        sortOrder: p.sortOrder,
      })),

      testimonials: formData.testimonials.map((t) => ({
        _id: t._id,
        name: t.name || "",
        role: t.role || "",
        quote: t.quote || t.review || "",
        review: t.review || t.quote || "",
        rating: t.rating !== undefined ? t.rating : 5,
        sortOrder: t.sortOrder,
        image: typeof t.image === "string" ? t.image : "",
        hasNewFile: t.imageFile instanceof File || t.image instanceof File,
      })),
    };
  }

  const hasUnsavedChanges = computed(() => {
    return JSON.stringify(getCleanState()) !== initialStateJson.value;
  });

  function snapshotState() {
    initialStateJson.value = JSON.stringify(getCleanState());
  }

  /* Pillar Methods */
  function addPillar() {
    const nextIdx = formData.pillars.length;
    formData.pillars.push({
      num: String.fromCharCode(65 + nextIdx) + ".",
      icon: "fa-graduation-cap",
      title: "New Commitment Pillar",
      description: "",
      sortOrder: nextIdx,
    });
  }

  function removePillar(index) {
    formData.pillars.splice(index, 1);
  }

  /* Testimonial Methods */
  function addTestimonial() {
    formData.testimonials.push({
      name: "",
      role: "Parent",
      quote: "",
      review: "",
      rating: 5,
      image: "",
      imageFile: null,
      previewUrl: "",
      sortOrder: formData.testimonials.length,
    });
  }

  function removeTestimonial(index) {
    formData.testimonials.splice(index, 1);
  }

  function handleTestimonialPhotoSelect(event, index) {
    const file = event.target.files[0];
    if (!file) return;
    formData.testimonials[index].imageFile = file;
    formData.testimonials[index].previewUrl = URL.createObjectURL(file);
  }

  function removeTestimonialPhoto(index) {
    formData.testimonials[index].imageFile = null;
    formData.testimonials[index].previewUrl = "";
    formData.testimonials[index].image = "";
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details =
        store.state.website?.details || store.state.websiteDetails || {};

      // Parse Pillars
      if (Array.isArray(details.pillars) && details.pillars.length > 0) {
        formData.pillars = details.pillars.map((p, idx) => ({
          _id: p._id,
          num: p.num || String.fromCharCode(65 + idx) + ".",
          icon: p.icon || "fa-graduation-cap",
          title: p.title || "",
          description: p.description || "",
          sortOrder: p.sortOrder !== undefined ? p.sortOrder : idx,
        }));
      } else {
        formData.pillars = [
          {
            num: "A.",
            icon: "fa-graduation-cap",
            title: "Comprehensive Curriculum",
            description:
              "We believe in holistic development — teachers here serve as guides, mentors and counsellors, not just instructors.",
            sortOrder: 0,
          },
          {
            num: "B.",
            icon: "fa-building-columns",
            title: "Modern Infrastructure",
            description:
              "World-class facilities spread across 7 acres, with careful safety protocols built into every corner of campus.",
            sortOrder: 1,
          },
          {
            num: "C.",
            icon: "fa-volleyball",
            title: "Beyond Academics",
            description:
              "Over 30 sports activities and 10 student clubs, built for the kind of growth that doesn't show up on a report card.",
            sortOrder: 2,
          },
        ];
      }

      // Parse Testimonials
      if (
        Array.isArray(details.testimonials) &&
        details.testimonials.length > 0
      ) {
        formData.testimonials = details.testimonials.map((t, idx) => ({
          _id: t._id,
          name: t.name || "",
          role: t.role || "Parent",
          quote: t.quote || t.review || "",
          review: t.review || t.quote || "",
          rating: t.rating !== undefined ? t.rating : 5,
          image: t.image || "",
          imageFile: null,
          previewUrl: "",
          sortOrder: t.sortOrder !== undefined ? t.sortOrder : idx,
        }));
      } else {
        formData.testimonials = [
          {
            name: "Dr. Mayuri & Neelav Chaliha",
            role: "Parent",
            quote:
              "DPS Tezpur provides excellent facilities and co-curricular activities, resulting in a genuinely positive learning environment.",
            review:
              "DPS Tezpur provides excellent facilities and co-curricular activities, resulting in a genuinely positive learning environment.",
            rating: 5,
            image:
              "https://dpstezpur.com/wp-content/uploads/2023/03/Dr_Mayuri-1.jpg",
            imageFile: null,
            previewUrl: "",
            sortOrder: 0,
          },
        ];
      }

      snapshotState();
    } catch (err) {
      showToast("Failed to load pillars & testimonials data", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    isSaving.value = true;
    try {
      const payload = {
        pillars: formData.pillars.map((p, idx) => ({
          _id: p._id,
          num: p.num || `${String.fromCharCode(65 + idx)}.`,
          icon: p.icon || "fa-graduation-cap",
          title: p.title || "",
          description: p.description || "",
          sortOrder: p.sortOrder !== undefined ? p.sortOrder : idx,
        })),

        testimonials: formData.testimonials.map((t, idx) => {
          const file =
            t.imageFile instanceof File
              ? t.imageFile
              : t.image instanceof File
              ? t.image
              : null;

          return {
            _id: t._id,
            name: t.name || "",
            role: t.role || "Parent",
            quote: t.quote || t.review || "",
            review: t.review || t.quote || "",
            rating: t.rating !== undefined ? t.rating : 5,
            sortOrder: t.sortOrder !== undefined ? t.sortOrder : idx,
            image: file || t.image || "",
          };
        }),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await loadData();
      showToast(
        "Shaping Bright Futures & Parent Testimonials saved successfully!",
        "success",
      );
    } catch (err) {
      showToast(err.message || "Error saving details", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Reset to saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }
</style>
